import { reloadOnce } from './reloadGuard';
import { isRestingPath } from './swReload';

/**
 * The service-worker update protocol.
 *
 * A worker that updates carelessly is worse than no worker: it can swap the code under a
 * half-filled order form, or reload a tab mid-payment. The rules below exist to make every step
 * deliberate, and each one is here because the obvious shortcut breaks something specific.
 *
 * 1. ~~**The worker never calls `skipWaiting()` in `install`.**~~ **ОТМЕНЕНО 12.09.2026.** Правило
 *    стоило работающей оплаты: починка политики безопасности лежала в кэшированной странице, а
 *    согласиться на обновление было негде — по правилу 6 предложение не показывается как раз во
 *    время оплаты. Теперь worker активируется сам, а вкладка в покое перезагружает себя по
 *    `controllerchange` (`swReload.ts` знает, какие вкладки в покое); незаконченный заказ защищает
 *    черновик на диске, а не отложенное обновление. Обоснование целиком — в `sw.ts`. Занятая
 *    вкладка получает предложение обновиться, как и раньше.
 * 2. **Check for updates on focus and every 30 minutes.** A PWA on a phone is resumed, not
 *    reloaded; without an explicit check a tab can run an old build for days.
 * 3. **A waiting worker is announced, not applied.** The person decides when to take it.
 * 4. **Applying it is: `SKIP_WAITING` → `controllerchange` → exactly one reload**, guarded by the
 *    shared brake in `reloadGuard.ts` so this cannot ping-pong with the stale-chunk reload.
 * 5. **A failed dynamic import offers a reload** — `chunkReload.ts`.
 * 6. **The prompt is never shown during the order wizard or payment.** Owned by the UI, since only
 *    it knows where the person is; see `UpdatePrompt.tsx`.
 *
 * This is a factory rather than a module with global state so it can be driven by a fake in tests
 * — none of jsdom, happy-dom or a headless browser gives you a second waiting worker on demand.
 */

/** Built by `vite-plugin-pwa` from `src/sw.ts`. */
const SW_URL = '/sw.js';

/** Rule 2. Half an hour is often enough to catch a deploy, rare enough to be invisible. */
const UPDATE_INTERVAL_MS = 30 * 60 * 1000;

/** A resumed phone can fire `visibilitychange` in bursts; one check a minute is plenty. */
const FOCUS_CHECK_THROTTLE_MS = 60 * 1000;

/**
 * How long to wait for `controllerchange` before reloading anyway.
 *
 * `skipWaiting()` normally answers in milliseconds, but a worker wedged by a hung `waitUntil`
 * never does, and then the button the person just pressed appears to have done nothing at all.
 * Reloading regardless is safe: the shared guard means it still happens only once.
 */
const CONTROLLER_CHANGE_TIMEOUT_MS = 3_000;

export type UpdateListener = (ready: boolean) => void;

export interface SwUpdateController {
  /** Fires with `true` once a newer version is installed and waiting. */
  subscribe(listener: UpdateListener): () => void;
  /** Take the waiting version now: skip waiting, then reload once. */
  apply(): void;
  /** Drop timers and listeners. */
  stop(): void;
}

/**
 * The one controller the app uses.
 *
 * A worker registration belongs to the page, not to a React component. Creating it per mount
 * meant that unmounting — which StrictMode does on purpose in development — called `stop()` and
 * left a permanently deaf controller behind. Components subscribe to this; nothing stops it.
 */
let shared: SwUpdateController | null = null;

export function sharedSwUpdateController(): SwUpdateController {
  shared ??= createSwUpdateController();
  return shared;
}

export function createSwUpdateController(
  container: ServiceWorkerContainer | undefined = typeof navigator === 'undefined'
    ? undefined
    : navigator.serviceWorker,
): SwUpdateController {
  const listeners = new Set<UpdateListener>();
  let registration: ServiceWorkerRegistration | null = null;
  let ready = false;
  /** True between pressing «обновить» and the reload — tells our own change from another tab's. */
  let applying = false;
  let lastFocusCheck = 0;
  let interval: ReturnType<typeof setInterval> | undefined;
  let stopped = false;

  function announce(): void {
    if (!ready) return;
    for (const listener of listeners) listener(true);
  }

  function markReady(): void {
    if (ready) return;
    ready = true;
    announce();
  }

  function checkForUpdate(): void {
    // `update()` rejects when the device is offline or the server is unreachable. That is a
    // non-event: we simply have not learned about a new version yet.
    void registration?.update().catch(() => undefined);
  }

  function watchInstalling(worker: ServiceWorker | null): void {
    if (!worker) return;
    worker.addEventListener('statechange', () => {
      // `installed` with an existing controller means «a newer build is ready and waiting».
      // `installed` with NO controller is the very first install — there is nothing to announce,
      // the page is already running the only build there is.
      if (worker.state === 'installed' && container?.controller) markReady();
    });
  }

  /** Была ли вкладка под worker'ом при загрузке: без него `controllerchange` — первая установка. */
  const wasControlled = Boolean(container?.controller);

  const onControllerChange = (): void => {
    if (applying) {
      reloadOnce();
      return;
    }
    // Первая установка: страница уже работает на единственной существующей сборке, перезагружать
    // и предлагать нечего.
    if (!wasControlled) return;
    // Новый worker взял вкладку под управление сам (см. `sw.ts`): вкладка в покое перезагружается
    // сразу, чтобы получить свежую оболочку с политикой безопасности; вкладка посреди дела получает
    // предложение — то же самое, и причина та же.
    if (isRestingPath(window.location.pathname)) {
      reloadOnce();
      return;
    }
    markReady();
  };

  const onVisibilityChange = (): void => {
    if (document.visibilityState !== 'visible') return;
    const now = Date.now();
    if (now - lastFocusCheck < FOCUS_CHECK_THROTTLE_MS) return;
    lastFocusCheck = now;
    checkForUpdate();
  };

  if (container) {
    container.addEventListener('controllerchange', onControllerChange);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onVisibilityChange);

    void container
      .register(SW_URL)
      .then((reg) => {
        if (stopped) return;
        registration = reg;
        // A worker can already be waiting from a previous visit — the browser keeps it.
        if (reg.waiting && container.controller) markReady();
        reg.addEventListener('updatefound', () => watchInstalling(reg.installing));
        interval = setInterval(checkForUpdate, UPDATE_INTERVAL_MS);
      })
      .catch(() => {
        // Registration fails on an insecure origin, with the worker file missing, or when the
        // browser has service workers disabled. The app is fully functional without one; this is
        // the offline/installable layer, not a dependency.
      });
  }

  return {
    subscribe(listener) {
      listeners.add(listener);
      // A subscriber arriving after the fact still needs to know.
      if (ready) listener(true);
      return () => listeners.delete(listener);
    },

    apply() {
      const waiting = registration?.waiting;
      if (!waiting) {
        // Already activated — by another tab, or because the worker moved on while the prompt was
        // on screen. The reload is still the right action and still the thing that was asked for.
        reloadOnce();
        return;
      }
      applying = true;
      waiting.postMessage({ type: 'SKIP_WAITING' });
      setTimeout(() => {
        if (applying) reloadOnce();
      }, CONTROLLER_CHANGE_TIMEOUT_MS);
    },

    stop() {
      stopped = true;
      listeners.clear();
      if (interval !== undefined) clearInterval(interval);
      container?.removeEventListener('controllerchange', onControllerChange);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onVisibilityChange);
    },
  };
}
